// src/app/login/msal-auth.service.ts
import { Injectable, Optional } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MsalService } from '@azure/msal-angular';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { environment } from '../../environments/environment';
import { LocalStorageService } from 'app/core/mips/common/services/local-storage.service';
import { ActionTypes } from 'app/core/store/logoutUser/logoutUser.action';
import { LogoutService } from 'app/core/mips/common/services/logout.service';

const API_SCOPE = 'api://2568cd0c-d5a2-4571-845d-e5c7dea54707/access_as_user';
const LOGIN_SCOPES = ['openid', 'profile', API_SCOPE];
const TOKEN_SCOPES = [API_SCOPE];
const AUTHENTICATED_THIS_LOAD = 'msal_authenticated_this_load';
const MSAL_JWT_TOKEN = 'MSAL_JWT_TOKEN';
const API_KEY = 'API_KEY';

@Injectable({
  providedIn: 'root'
})
export class MsalAuthService {
  private loginRequest$: Observable<any> | null = null;
  private refreshInProgress$: Observable<string | null> | null = null;

  constructor(
    private msalService: MsalService,
    private http: HttpClient,
    private store: Store,
    @Optional() private logoutService?: LogoutService
  ) {}

  /**
   * Start the full-page Microsoft sign-in redirect.
   */
  login(forceInteractive: boolean = false): Observable<any> {
    if (!forceInteractive && this.isLoggedIn()) {
      return of({ account: this.msalService.instance.getActiveAccount() });
    }

    if (this.loginRequest$) {
      return this.loginRequest$;
    }

    const loginRequest: any = {
      scopes: LOGIN_SCOPES,
      prompt: 'login'
    };

    this.loginRequest$ = this.msalService.loginRedirect(loginRequest).pipe(
      catchError(error => {
        console.error('MSAL login error:', error);
        throw error;
      }),
      finalize(() => this.loginRequest$ = null),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    return this.loginRequest$;
  }

  /**
   * Check if user is logged in (multi-tab and token validity aware)
   */
  isLoggedIn(): boolean {
    const activeAccount = this.msalService.instance.getActiveAccount() ||
      this.msalService.instance.getAllAccounts()[0];

    if (!activeAccount) {
      return false;
    }

    const token = LocalStorageService.getLocalStorageValueBasedOnKeys(MSAL_JWT_TOKEN) ||
                  LocalStorageService.getLocalStorageValueBasedOnKeys(API_KEY);

    // If account exists and token is present and not expired, user is validly logged in
    if (token && !this.isTokenExpiringSoon(token, 0)) {
      if (!this.msalService.instance.getActiveAccount()) {
        this.msalService.instance.setActiveAccount(activeAccount);
        sessionStorage.setItem(AUTHENTICATED_THIS_LOAD, 'true');
      }
      return true;
    }

    // Only count as logged in during initial token acquisition if an active MSAL redirect is in progress
    if (sessionStorage.getItem('MSAL_REDIRECT_IN_PROGRESS') === 'true') {
      if (!this.msalService.instance.getActiveAccount()) {
        this.msalService.instance.setActiveAccount(activeAccount);
      }
      return true;
    }

    return false;
  }

  public authenticateAndSync(): Observable<any> {
    if (!this.isLoggedIn()) {
      return throwError(() => new Error('No authenticated Microsoft account is available'));
    }

    return this.getTokenSilent().pipe(
      switchMap(token => {
        if (!token) {
          return throwError(() => new Error('Microsoft token acquisition failed'));
        }

        LocalStorageService.setLocalStorageValueBasedOnKey('API_KEY', token);
        LocalStorageService.setLocalStorageValueBasedOnKey(MSAL_JWT_TOKEN, token);

        return this.http.post<any>(`${environment.apiURLEndpoint}api/msal-login`, {}, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }),
      tap(userData => {
        if (userData?.api_key) {
          LocalStorageService.setLocalStorageValueBasedOnKey('API_KEY', userData.api_key);
        }
      }),
      catchError(error => {
        console.error('MSAL backend sync error — status:', error?.status, 'body:', error?.error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Sync with backend after MSAL authentication
   */
  syncUserWithBackend(): Observable<any> {
    return this.authenticateAndSync();
  }

  /**
   * Get an API token silently without interrupting the user.
   * If interaction is required or token cannot be renewed silently, it throws an error
   * rather than forcing an unexpected full-page redirect.
   */
  public getTokenSilent(forceRefresh: boolean = false): Observable<string | null> {
    const activeAccount = this.msalService.instance.getActiveAccount() ||
      this.msalService.instance.getAllAccounts()[0];

    if (!activeAccount) {
      console.warn('No authenticated MSAL account is available for silent token acquisition');
      return of(null);
    }

    if (!this.msalService.instance.getActiveAccount()) {
      this.msalService.instance.setActiveAccount(activeAccount);
    }

    const request = {
      scopes: TOKEN_SCOPES,
      account: activeAccount,
      forceRefresh: forceRefresh
    };

    return from(this.msalService.acquireTokenSilent(request)).pipe(
      map(response => response?.accessToken || null),
      catchError(error => {
        console.error('Error acquiring token silently:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Helper to check if a JWT token is expired or expiring soon (within bufferSeconds)
   */
  public isTokenExpiringSoon(token: string | null, bufferSeconds: number = 300): boolean {
    if (!token) return true;
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return true;
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      const payload = JSON.parse(jsonPayload);
      if (!payload.exp) return false;
      const nowSeconds = Math.floor(Date.now() / 1000);
      return (payload.exp - nowSeconds) < bufferSeconds;
    } catch (e) {
      return true;
    }
  }

  /**
   * Proactive token fetcher: returns valid cached token or refreshes silently if expiring within bufferSeconds.
   */
  public getValidTokenProactively(bufferSeconds: number = 300): Observable<string | null> {
    const cachedToken = LocalStorageService.getLocalStorageValueBasedOnKeys(MSAL_JWT_TOKEN) || LocalStorageService.getLocalStorageValueBasedOnKeys(API_KEY);
    if (cachedToken && !this.isTokenExpiringSoon(cachedToken, bufferSeconds)) {
      return of(cachedToken);
    }
    return this.refreshToken(false);
  }

  /**
   * Refresh the token and update storage with deduplication of in-flight requests.
   */
  public refreshToken(forceRefresh: boolean = false): Observable<string | null> {
    if (this.refreshInProgress$) {
      return this.refreshInProgress$;
    }

    this.refreshInProgress$ = this.getTokenSilent(forceRefresh).pipe(
      map(token => {
        if (token) {
          LocalStorageService.setLocalStorageValueBasedOnKey('API_KEY', token);
          LocalStorageService.setLocalStorageValueBasedOnKey(MSAL_JWT_TOKEN, token);
        }
        return token;
      }),
      finalize(() => {
        this.refreshInProgress$ = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    return this.refreshInProgress$;
  }

  clearAuthenticationState(): void {
    LocalStorageService.removeLocalStorageValueBasedOnKeys(API_KEY);
    LocalStorageService.removeLocalStorageValueBasedOnKeys(MSAL_JWT_TOKEN);
    LocalStorageService.removeLocalStorageValueBasedOnKeys('currentUser');
    LocalStorageService.removeLocalStorageValueBasedOnKeys('loggedInUser');
    sessionStorage.removeItem(AUTHENTICATED_THIS_LOAD);
    this.store.dispatch({ type: ActionTypes.LOGOUT });
    this.logoutService?.beginLogoutAction?.next(true);
    this.msalService.instance?.setActiveAccount?.(null);
  }

  public logout(): Observable<void> {
    const account = this.msalService.instance.getActiveAccount();
    this.clearAuthenticationState();
    this.msalService.instance.setActiveAccount(null);

    return from(this.msalService.logoutRedirect({ account })).pipe(
      tap(() => {
        const instance = this.msalService.instance as unknown as {
          clearCache?: () => Promise<void>;
        };

        void instance.clearCache?.();
        this.clearAuthenticationState();
      })
    );
  }
}
